import React, { useEffect, useImperativeHandle, useRef } from 'react';

import ChunkedVideoRecorder from '@components/shared/ChunkedVideoRecorder';
import type { ChunkedVideoRecorderHandle } from '@components/shared/ChunkedVideoRecorder';
import type { Subtitle, Magnifier, SessionMeta } from '@hooks/operator/useChunkedVideoRecorder';
import type { ChunkTask, FinalizeTask } from '@hooks/operator/useChunkedUploadQueue';

export interface ReturnVerificationUploadQueue {
  enqueueChunk: (task: Omit<ChunkTask, 'type'>) => boolean;
  enqueueFinalize: (task: Omit<FinalizeTask, 'type'>) => void;
}

export interface ReturnVerificationVideoRecorderHandle {
  startRecording: () => Promise<void>;
  cancelRecording: () => void;
  // High-level: stop the current chunked session and enqueue finalize bound to
  // the supplied return_receipt. The page calls this exactly once when the
  // operator confirms verification — the activeReceiptId at that moment is the
  // receipt the just-uploaded chunks belong to.
  stopAndFinalize: (returnReceiptId: string | number) => Promise<SessionMeta | null>;
  readonly isRecording: boolean;
  readonly videoElement: HTMLVideoElement | null;
}

export interface ReturnVerificationVideoRecorderProps {
  orderNumber: string;
  subtitle?: Subtitle | null;
  magnifier?: Magnifier | null;
  autoStart?: boolean;
  placeholderText?: string;
  returnReceiptId: string | number | null;
  uploadQueue: ReturnVerificationUploadQueue;
}

/**
 * 退貨驗收專用錄影外殼 — wrap @shared/ChunkedVideoRecorder。
 *
 * 對 QC 版的差異：
 *   - 一次驗收 = 一支影片，不像 QC 是「每 parcel 一支」，所以沒有 splitter，也沒有
 *     COMPLETED 倒數的概念。
 *   - chunk 進來 → enqueueChunk(entityId=returnReceiptId)；queue 滿了就 cancel
 *     錄影避免 chunk 丟掉導致 total 對不起來。
 *   - stopAndFinalize 是 high-level API：呼叫者（ReturnOrderVerification/Index）
 *     在驗收 confirm 時呼叫一次，內部 stop + enqueueFinalize（無 parcel_id / video_index）。
 *
 * returnReceiptId 是「之後才會出現」的：自從改成延遲建單以後（避免 orphan
 * :pending receipt），整個掃描期間 returnReceiptId 都是 null，直到使用者按「確認
 * 驗收」、init_draft 回來才設定。為了不丟掉掃描期間的影片，這支元件在 id 還是
 * null 時把 chunks 暫存在 pendingChunksRef，等 id 一進來就用同樣的 sessionId
 * 把 buffered chunks 依序灌進 uploadQueue（backend concat_video_worker 依
 * sessionId 拼回去，順序不變即可）。cancelRecording 會把 buffer 也清掉。
 */
const ReturnVerificationVideoRecorder = React.forwardRef<
  ReturnVerificationVideoRecorderHandle,
  ReturnVerificationVideoRecorderProps
>(({
  orderNumber,
  subtitle,
  magnifier,
  autoStart,
  placeholderText,
  returnReceiptId,
  uploadQueue,
}, ref) => {
  const innerRef = useRef<ChunkedVideoRecorderHandle | null>(null);
  const returnReceiptIdRef = useRef(returnReceiptId);
  returnReceiptIdRef.current = returnReceiptId;
  const isRecordingRef = useRef(false);
  // Chunks 在 returnReceiptId 還是 null 時暫存於此（依 onChunk 觸發順序排入），
  // id 一進來會 drain。記憶體成本以 typical 1-2 分鐘掃描 + 720p webm 估約 10-20MB；
  // queue overflow 時跟正常路徑一樣 cancelRecording。
  const pendingChunksRef = useRef<Array<{ blob: Blob; seq: number; sessionId: string }>>([]);

  // Drain buffered chunks when receiptId 終於進來。這個 effect 跟 handleChunk
  // 不衝突：handleChunk 在 id 進來後拿到的就是新 id，不會再 push 到 buffer。
  useEffect(() => {
    if (!returnReceiptId) return;
    if (pendingChunksRef.current.length === 0) return;

    const drained = pendingChunksRef.current;
    pendingChunksRef.current = [];
    for (const c of drained) {
      const accepted = uploadQueue.enqueueChunk({
        entityId: returnReceiptId,
        sessionId: c.sessionId,
        seq: c.seq,
        blob: c.blob,
      });
      if (!accepted) {
        innerRef.current?.cancelRecording();
        return;
      }
    }
  }, [returnReceiptId, uploadQueue]);

  useImperativeHandle(ref, () => ({
    startRecording: async () => { await innerRef.current?.startRecording(); },
    cancelRecording: () => {
      pendingChunksRef.current = [];
      innerRef.current?.cancelRecording();
    },
    stopAndFinalize: async (id) => {
      const meta = await innerRef.current?.stopRecording();
      if (!meta || !id) return meta ?? null;
      // stopRecording 不會 fire 新 chunk；殘留在 buffer 的最後幾筆若 useEffect
      // 還沒 drain（id 剛剛才設定），這裡同步 drain 一次再 enqueueFinalize，
      // 避免 totalChunks 跟實際上傳數量對不起來。
      if (pendingChunksRef.current.length > 0) {
        const drained = pendingChunksRef.current;
        pendingChunksRef.current = [];
        for (const c of drained) {
          uploadQueue.enqueueChunk({ entityId: id, sessionId: c.sessionId, seq: c.seq, blob: c.blob });
        }
      }
      uploadQueue.enqueueFinalize({
        entityId: id,
        sessionId: meta.sessionId,
        totalChunks: meta.totalChunks,
      });
      return meta;
    },
    get isRecording() { return isRecordingRef.current; },
    get videoElement() { return innerRef.current?.videoElement ?? null; },
  }), [ uploadQueue ]);

  const handleChunk = (blob: Blob, seq: number, sessionId: string) => {
    const id = returnReceiptIdRef.current;
    if (!id) {
      // 還沒按「確認驗收」、init_draft 還沒回來；先 buffer 起來，等 id 進來
      // useEffect 會 drain。sessionId 在單次錄影中穩定，drain 時順序保留。
      pendingChunksRef.current.push({ blob, seq, sessionId });
      return;
    }
    const accepted = uploadQueue.enqueueChunk({
      entityId: id,
      sessionId,
      seq,
      blob,
    });
    if (!accepted) {
      // Queue 滿了。停掉錄影避免後續 chunks 被 queue 丟掉、導致 finalize 時
      // total_chunks 對不起來。Modal 已由 useReturnReceiptChunkedUploadQueue
      // 的 onQueueOverflow 彈出。
      innerRef.current?.cancelRecording();
    }
  };

  return (
    <ChunkedVideoRecorder
      ref={innerRef}
      orderNumber={orderNumber}
      subtitle={subtitle}
      magnifier={magnifier}
      autoStart={autoStart}
      placeholderText={placeholderText}
      onChunk={handleChunk}
      onRecordingChange={(rec) => { isRecordingRef.current = rec; }}
    />
  );
});

ReturnVerificationVideoRecorder.displayName = 'ReturnVerificationVideoRecorder';

export default ReturnVerificationVideoRecorder;
